import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Plus, Download, Sparkles,
  MessageSquare, Languages, Search, AlertTriangle,
  Loader2, Type, Sun, Clock, History, PanelRight, File, Trash2,
  Mic, MicOff, Printer, Send, FileUp,
} from "lucide-react";
import LanguageSelect from "@/components/LanguageSelect";
import { LANG_NAMES } from "@/config/languages";

// WorkDocs rich editor components
import WorkDocEditor from "@/components/workdocs/WorkDocEditor";
import WorkDocMenuBar from "@/components/workdocs/WorkDocMenuBar";
import WorkDocFormatToolbar from "@/components/workdocs/WorkDocFormatToolbar";
import WorkDocTableMenu from "@/components/workdocs/WorkDocTableMenu";
import WorkDocFindReplace from "@/components/workdocs/WorkDocFindReplace";
import WorkDocStatusBar from "@/components/workdocs/WorkDocStatusBar";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function WorkDocs() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const [editorJson, setEditorJson] = useState(null);
  const [title, setTitle] = useState("Untitled");
  const [showCopilot, setShowCopilot] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);

  // Editor stats
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  // Copilot state
  const [copilotPrompt, setCopilotPrompt] = useState("");
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [copilotResponse, setCopilotResponse] = useState(null);

  // AI Features
  const [translateTarget, setTranslateTarget] = useState("fr");
  const [selectedTheme, setSelectedTheme] = useState("modern_corporate");
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [auditLog, setAuditLog] = useState([]);

  // Templates & Themes
  const [templates, setTemplates] = useState([]);
  const [themes, setThemes] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("blank");

  // Dictation
  const [isDictating, setIsDictating] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [dictationLang, setDictationLang] = useState("en");
  const dictationLangRef = useRef("en");
  const isDictatingRef = useRef(false);
  const recognitionRef = useRef(null);

  // TipTap editor ref
  const editorInstanceRef = useRef(null);
  const fileInputRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { dictationLangRef.current = dictationLang; }, [dictationLang]);
  useEffect(() => { isDictatingRef.current = isDictating; }, [isDictating]);

  const DICTATION_LANG_MAP = {
    "en": "en-US", "es": "es-ES", "fr": "fr-FR", "pt": "pt-PT", "de": "de-DE",
    "it": "it-IT", "nl": "nl-NL", "ar": "ar-SA", "zh": "zh-CN", "ru": "ru-RU",
    "hi": "hi-IN", "ur": "ur-IN", "fa": "fa-IR", "he": "he-IL",
    "sw": "sw-TZ", "ha": "ha-NG", "yo": "yo-NG", "ig": "ig-NG",
    "sn": "sn-ZW",
    "zu": "zu-ZA", "xh": "xh-ZA", "af": "af-ZA", "st": "st-ZA", "tn": "tn-ZA",
  };
  const resolveDictationLang = (code) => DICTATION_LANG_MAP[code] || "en-US";

  // ── Init ──
  useEffect(() => { fetchDocuments(); fetchTemplates(); fetchThemes(); }, []);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/api/smart-office/workdocs`, { headers: getHeaders(), withCredentials: true });
      setDocuments(res.data.documents || []);
    } catch {}
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/smart-office/workdocs/templates`, { headers: getHeaders(), withCredentials: true });
      setTemplates(res.data.templates || []);
    } catch {}
  };

  const fetchThemes = async () => {
    try {
      const res = await axios.get(`${API}/api/smart-office/workdocs/themes`, { headers: getHeaders(), withCredentials: true });
      setThemes(res.data.themes || []);
    } catch {}
  };

  // ── Editor callback ──
  const handleEditorReady = useCallback((editor) => {
    editorInstanceRef.current = editor;
  }, []);

  const handleEditorUpdate = useCallback(({ json, wordCount: wc, charCount: cc }) => {
    if (json) setEditorJson(json);
    setWordCount(wc || 0);
    setCharCount(cc || 0);
  }, []);

  // ── Document CRUD ──
  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/api/smart-office/workdocs/create`, {
        title: "Untitled Document",
        template: selectedTemplate,
      }, { headers: getHeaders(), withCredentials: true });
      setActiveDoc(res.data);
      setTitle(res.data.title);
      setEditorJson(res.data.content_json);
      setCopilotResponse(null);
      setAnalysisResult(null);
      fetchDocuments();
      toast.success("Document created");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Please log in first");
        return;
      }
      const msg = err.response?.data?.detail || err.response?.data || err.message || "Failed to create document";
      const display = typeof msg === 'string' ? msg : JSON.stringify(msg);
      console.error("WorkDocs error:", err.response?.status, display);
      toast.error(display.substring(0, 200));
    }
  };

  const handleOpen = async (doc) => {
    try {
      const res = await axios.get(`${API}/api/smart-office/workdocs/${doc.id}`, { headers: getHeaders(), withCredentials: true });
      setActiveDoc(res.data);
      setTitle(res.data.title);
      setEditorJson(res.data.content_json);
      setCopilotResponse(null);
      setAnalysisResult(null);
      setShowSidebar(false);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(msg || "Failed to load document");
    }
  };

  const handleSave = async () => {
    if (!activeDoc || !editorInstanceRef.current) return;
    const editor = editorInstanceRef.current;
    const contentJson = editor.getJSON();
    try {
      await axios.put(`${API}/api/smart-office/workdocs/${activeDoc.id}`, {
        content_json: contentJson,
        title: title,
      }, { headers: getHeaders(), withCredentials: true });
      toast.success("Saved");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/api/smart-office/workdocs/${docId}`, { headers: getHeaders(), withCredentials: true });
      if (activeDoc?.id === docId) { setActiveDoc(null); setEditorJson(null); }
      fetchDocuments();
      toast.success("Deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleExport = async (fmt) => {
    if (!activeDoc) return;
    try {
      const form = new FormData();
      form.append("fmt", fmt);
      const res = await axios.post(`${API}/api/smart-office/workdocs/${activeDoc.id}/export`, form, {
        headers: getHeaders(), withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeDoc.title || "document"}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  const handlePrint = () => {
    if (!editorInstanceRef.current) return;
    const content = editorInstanceRef.current.getHTML();
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html><head><title>${title || "Document"}</title>
        <style>
          body { font-family: 'Times New Roman', serif; margin: 2.54cm; color: #000; background: #fff; }
          table { border-collapse: collapse; width: 100%; margin: 1em 0; }
          td, th { border: 1px solid #999; padding: 6px; }
          @media print { body { margin: 2.54cm; } }
        </style></head>
        <body>${content}</body></html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 500);
    }
  };

  const handleImportDocx = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!editorInstanceRef.current) return;
    try {
      if (file.name.endsWith(".docx")) {
        // Client-side DOCX import via mammoth
        const mammoth = await import("mammoth");
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        editorInstanceRef.current.commands.setContent(result.value, false);
        toast.success("DOCX imported successfully");
      } else if (file.name.endsWith(".txt") || file.name.endsWith(".md")) {
        const text = await file.text();
        editorInstanceRef.current.commands.setContent(text);
        toast.success("File loaded into editor");
      } else {
        toast.error("Unsupported file format");
      }
    } catch (err) {
      toast.error("Import failed: " + (err.message || "unknown error"));
    }
    e.target.value = "";
  };

  // ── Dictation ──
  const startDictation = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Speech recognition not supported"); return; }
    const r = new SR();
    const bcp47 = resolveDictationLang(dictationLangRef.current);
    r.continuous = true; r.interimResults = true; r.lang = bcp47;
    r.onresult = (e) => {
      let interim = "", final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) { final += e.results[i][0].transcript + " "; }
        else { interim += e.results[i][0].transcript; }
      }
      if (final && editorInstanceRef.current) {
        editorInstanceRef.current.chain().focus().insertContent(final).run();
      }
      setInterimText(interim);
    };
    r.onerror = () => { setIsDictating(false); toast.error("Dictation error"); };
    r.onend = () => { if (isDictatingRef.current) { try { r.start(); } catch {} } };
    recognitionRef.current = r; r.start(); setIsDictating(true); isDictatingRef.current = true;
    toast.success("Dictation started — " + (LANG_NAMES[dictationLangRef.current] || dictationLangRef.current));
  };
  const stopDictation = () => {
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} recognitionRef.current = null; }
    setIsDictating(false); isDictatingRef.current = false; setInterimText("");
  };
  const toggleDictation = () => { isDictating ? stopDictation() : startDictation(); };

  // ── AI Features ──
  const handleAiEdit = async () => {
    if (!activeDoc || !copilotPrompt.trim()) return;
    setCopilotLoading(true);
    try {
      const res = await axios.post(`${API}/api/smart-office/workdocs/${activeDoc.id}/ai-edit`, {
        prompt: copilotPrompt,
      }, { headers: getHeaders(), withCredentials: true });
      setCopilotResponse(res.data);
      if (res.data.content_json && editorInstanceRef.current) {
        editorInstanceRef.current.commands.setContent(res.data.content_json, false);
      }
      toast.success(res.data.summary || "AI edit applied");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI edit failed");
    }
    setCopilotLoading(false);
  };

  const handleAiFormat = async () => {
    if (!activeDoc) return;
    try {
      const res = await axios.post(`${API}/api/smart-office/workdocs/${activeDoc.id}/ai-format`, {
        theme: selectedTheme,
      }, { headers: getHeaders(), withCredentials: true });
      toast.success(`Theme applied: ${res.data.theme}`);
    } catch { toast.error("Theme application failed"); }
  };

  const handleAiTranslate = async () => {
    if (!activeDoc) return;
    try {
      const res = await axios.post(`${API}/api/smart-office/workdocs/${activeDoc.id}/translate`, {
        target_language: translateTarget,
      }, { headers: getHeaders(), withCredentials: true });
      if (res.data.content_json && editorInstanceRef.current) {
        editorInstanceRef.current.commands.setContent(res.data.content_json, false);
      }
      toast.success(res.data.summary || "Translated");
    } catch { toast.error("Translation failed"); }
  };

  const handleAnalyze = async () => {
    if (!activeDoc) return;
    setAnalysisLoading(true);
    try {
      const res = await axios.post(`${API}/api/smart-office/workdocs/${activeDoc.id}/analyze`, {}, { headers: getHeaders(), withCredentials: true });
      setAnalysisResult(res.data);
    } catch { toast.error("Analysis failed"); }
    setAnalysisLoading(false);
  };

  const handleLoadAudit = async () => {
    if (!activeDoc) return;
    try {
      const res = await axios.get(`${API}/api/smart-office/workdocs/${activeDoc.id}/audit`, { headers: getHeaders(), withCredentials: true });
      setAuditLog(res.data.audit_log || []);
    } catch {}
  };

  // ── Dispatch to Smart Mail ──
  const handleDispatchToMail = async () => {
    if (!activeDoc || !editorInstanceRef.current) return;
    const html = editorInstanceRef.current.getHTML();
    const subject = prompt("Email subject:", title || "Document");
    if (!subject) return;
    const recipients = prompt("Recipient email(s) - comma separated:");
    if (!recipients) return;
    try {
      const res = await axios.post(`${API}/api/mail/send`, {
        data: JSON.stringify({
          to: recipients.split(",").map(e => ({ email: e.trim() })),
          subject,
          body: html,
          is_html: true,
        }),
      }, { headers: getHeaders(), withCredentials: true });
      toast.success("Document sent via Smart Mail");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/60 backdrop-blur px-4 py-2 flex items-center gap-3">
        <button onClick={() => navigate("/smart-office")} className="text-gray-400 hover:text-white p-1">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <FileText className="w-5 h-5 text-blue-400" />
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="h-8 bg-transparent border-0 text-white font-semibold text-lg focus:outline-none focus:ring-0 max-w-md"
        />
        <div className="flex-1" />

        {activeDoc && (
          <>
            <Button variant="ghost" size="sm" onClick={handleDispatchToMail} className="text-gray-400" title="Dispatch to Smart Mail">
              <Send className="w-4 h-4 mr-1" /> Send
            </Button>
            <Button variant="ghost" size="sm" onClick={handlePrint} className="text-gray-400" title="Print">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowCopilot(!showCopilot)} className={`${showCopilot ? "text-blue-400" : "text-gray-400"}`}>
              <PanelRight className="w-4 h-4 mr-1" /> Copilot
            </Button>
          </>
        )}
        <Button variant="ghost" size="sm" onClick={() => setShowSidebar(!showSidebar)} className="text-gray-400">
          <File className="w-4 h-4 mr-1" /> Docs
        </Button>
        <input ref={fileInputRef} type="file" accept=".docx,.txt,.md" className="hidden" onChange={handleFileImport} />
      </div>

      {/* Menu Bar */}
      {activeDoc && (
        <WorkDocMenuBar
          editor={editorInstanceRef.current}
          onSave={handleSave}
          onExport={handleExport}
          onImport={handleImportDocx}
          onPrint={handlePrint}
          onDispatchToMail={handleDispatchToMail}
          onFindReplace={() => setShowFindReplace(!showFindReplace)}
          documentTitle={title}
        />
      )}

      {/* Find & Replace */}
      {activeDoc && (
        <WorkDocFindReplace
          editor={editorInstanceRef.current}
          open={showFindReplace}
          onClose={() => setShowFindReplace(false)}
        />
      )}

      {/* Format Toolbar */}
      {activeDoc && (
        <WorkDocFormatToolbar editor={editorInstanceRef.current} />
      )}

      {/* Table Menu — shows only when cursor is inside a table */}
      {activeDoc && (
        <WorkDocTableMenu editor={editorInstanceRef.current} />
      )}

      {/* Dictation toolbar (inline row below format toolbar) */}
      {activeDoc && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-900/40 border-b border-gray-800">
          <div className="flex items-center gap-1.5">
            <LanguageSelect value={dictationLang} onValueChange={setDictationLang} includeAuto={false} />
            <button onClick={toggleDictation}
              className={`p-1.5 rounded ${isDictating ? 'bg-red-600 text-white animate-pulse' : 'hover:bg-gray-800 text-gray-400'}`}
              title={isDictating ? "Stop dictation" : "Start dictation"}>
              {isDictating ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            {isDictating && (
              <span className="text-xs text-red-400 animate-pulse flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                Recording... {interimText && <span className="text-gray-500 italic">"{interimText}"</span>}
              </span>
            )}
          </div>
          <div className="flex-1" />
          <Select value={selectedTheme} onValueChange={setSelectedTheme}>
            <SelectTrigger className="h-7 bg-gray-800 border-gray-700 text-gray-300 text-xs w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700 text-white">
              {themes.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={handleAiFormat} className="text-gray-400 text-xs h-7">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Apply Theme
          </Button>
        </div>
      )}

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Document List */}
        {showSidebar && (
          <div className="w-64 border-r border-gray-800 bg-gray-900/30 p-3 overflow-y-auto flex-shrink-0">
            <div className="flex items-center gap-2 mb-3">
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger className="flex-1 h-8 bg-gray-800 border-gray-700 text-gray-300 text-xs">
                  <SelectValue placeholder="Template" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700 text-white">
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-500 h-8 w-8 p-0" onClick={handleCreate}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-1">
              {documents.map(doc => (
                <div key={doc.id} className={`flex items-center justify-between p-2 rounded cursor-pointer text-sm hover:bg-gray-800 ${activeDoc?.id === doc.id ? 'bg-gray-800 text-blue-400' : 'text-gray-400'}`}
                  onClick={() => handleOpen(doc)}>
                  <span className="truncate flex-1">{doc.title}</span>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }} className="text-gray-600 hover:text-red-400 ml-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Editor Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {!activeDoc ? (
            <div className="flex-1 flex items-center justify-center text-gray-600">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg mb-2">No document open</p>
                <p className="text-sm text-gray-700">Create a new document or open an existing one</p>
                <Button onClick={handleCreate} className="mt-4 bg-blue-600 hover:bg-blue-500">
                  <Plus className="w-4 h-4 mr-2" /> New Document
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-6 bg-gray-800/20">
              <WorkDocEditor
                content={editorJson}
                onEditorReady={handleEditorReady}
                onUpdate={handleEditorUpdate}
                placeholder="Start writing..."
                maxLength={100000}
              />
            </div>
          )}
        </div>

        {/* Copilot Sidebar */}
        {showCopilot && activeDoc && (
          <div className="w-80 border-l border-gray-800 bg-gray-900/30 flex flex-col overflow-hidden flex-shrink-0">
            <div className="p-3 border-b border-gray-800">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-400" /> Copilot
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {/* AI Edit */}
              <div className="space-y-2">
                <label className="text-xs text-gray-500 uppercase tracking-wider">AI Edit</label>
                <textarea
                  value={copilotPrompt}
                  onChange={(e) => setCopilotPrompt(e.target.value)}
                  placeholder="e.g. Rewrite the third paragraph to be more persuasive..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-blue-500 h-20"
                />
                <Button onClick={handleAiEdit} disabled={copilotLoading || !copilotPrompt.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white text-xs h-8">
                  {copilotLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Execute
                </Button>
              </div>

              {copilotResponse && (
                <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-3 space-y-2">
                  <p className="text-xs text-blue-300 font-medium">{copilotResponse.summary}</p>
                  {copilotResponse.changes?.map((c, i) => (
                    <p key={i} className="text-xs text-gray-400">• {c}</p>
                  ))}
                </div>
              )}

              <div className="h-px bg-gray-800" />

              {/* Translation */}
              <div className="space-y-2">
                <label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <Languages className="w-3 h-3" /> Translate Document
                </label>
                <div className="flex gap-2">
                  <LanguageSelect value={translateTarget} onValueChange={setTranslateTarget} includeAuto={false} />
                  <Button size="sm" onClick={handleAiTranslate} className="bg-green-600 hover:bg-green-500 text-white text-xs h-8">
                    Translate
                  </Button>
                </div>
              </div>

              <div className="h-px bg-gray-800" />

              {/* Analyze */}
              <div className="space-y-2">
                <label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <Search className="w-3 h-3" /> Analyze Document
                </label>
                <Button onClick={handleAnalyze} disabled={analysisLoading}
                  className="w-full bg-violet-600 hover:bg-violet-500 text-white text-xs h-8">
                  {analysisLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                  Analyze
                </Button>

                {analysisResult && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-gray-800/60 rounded p-2 text-center">
                        <p className="text-lg font-semibold text-white">{analysisResult.word_count || 0}</p>
                        <p className="text-[10px] text-gray-500">Words</p>
                      </div>
                      <div className="bg-gray-800/60 rounded p-2 text-center">
                        <p className="text-lg font-semibold text-white">{analysisResult.avg_sentence_length || 0}</p>
                        <p className="text-[10px] text-gray-500">Avg Sent.</p>
                      </div>
                    </div>
                    {analysisResult.passive_voice_instances > 0 && (
                      <div className="bg-amber-900/20 border border-amber-800/30 rounded-lg p-2">
                        <p className="text-xs text-amber-300 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {analysisResult.passive_voice_instances} passive voice instance(s)
                        </p>
                      </div>
                    )}
                    {analysisResult.vague_language_detected?.length > 0 && (
                      <div className="bg-amber-900/20 border border-amber-800/30 rounded-lg p-2">
                        <p className="text-xs text-amber-300">Vague: {analysisResult.vague_language_detected.join(", ")}</p>
                      </div>
                    )}
                    {analysisResult.ai_analysis?.summary && (
                      <div className="bg-gray-800/60 rounded-lg p-2">
                        <p className="text-xs text-gray-400">{analysisResult.ai_analysis.summary}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="h-px bg-gray-800" />

              {/* Audit Log */}
              <div className="space-y-2">
                <label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <History className="w-3 h-3" /> AI Audit Log
                </label>
                <Button onClick={handleLoadAudit} variant="outline" className="w-full border-gray-700 text-gray-300 text-xs h-8">
                  <Clock className="w-3 h-3 mr-1" /> Load History
                </Button>
                {auditLog.map((entry, i) => (
                  <div key={i} className="bg-gray-800/40 rounded p-2 border border-gray-700/50">
                    <p className="text-xs text-blue-300 font-medium truncate">{entry.original_prompt?.substring(0, 60)}...</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{entry.created_at?.substring(0, 10)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      {activeDoc && (
        <WorkDocStatusBar wordCount={wordCount} charCount={charCount} editor={editorInstanceRef.current} />
      )}
    </div>
  );
}
